import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotateX(0deg); }
  50% { transform: translateY(-20px) rotateX(10deg); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(0, 255, 0, 0.5); }
  50% { box-shadow: 0 0 40px rgba(0, 255, 0, 0.8); }
`;

const LockScreenContainer = styled.div<{ isUnlocking: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  transition: opacity 1s ease-out;
  opacity: ${props => props.isUnlocking ? 0 : 1};
  pointer-events: ${props => props.isUnlocking ? 'none' : 'all'};
`;

const Logo3D = styled.div`
  font-size: 4rem;
  color: #00ff00;
  text-shadow: 
    0 0 10px rgba(0, 255, 0, 0.8),
    0 0 20px rgba(0, 255, 0, 0.6),
    0 0 30px rgba(0, 255, 0, 0.4);
  animation: ${float} 3s ease-in-out infinite;
  margin-bottom: 2rem;
  perspective: 1000px;
  transform-style: preserve-3d;
`;

const WelcomeText = styled.h1`
  color: #00ffff;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  text-align: center;
  font-family: 'Ubuntu', sans-serif;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
`;

const SubText = styled.p`
  color: #cccccc;
  font-size: 1.2rem;
  margin-bottom: 3rem;
  text-align: center;
`;

const UnlockButton = styled.button`
  background: linear-gradient(45deg, rgba(0, 255, 0, 0.2), rgba(0, 255, 255, 0.2));
  border: 2px solid #00ff00;
  color: #00ff00;
  padding: 15px 40px;
  font-size: 1.2rem;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: ${glow} 2s ease-in-out infinite;
  font-family: 'Ubuntu', sans-serif;
  font-weight: bold;
  
  &:hover {
    background: linear-gradient(45deg, rgba(0, 255, 0, 0.4), rgba(0, 255, 255, 0.4));
    transform: scale(1.05);
    box-shadow: 0 0 30px rgba(0, 255, 0, 0.6);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const TimeDisplay = styled.div`
  position: absolute;
  top: 10%;
  right: 5%;
  color: #00ff00;
  font-size: 1.5rem;
  font-family: 'Courier New', monospace;
  text-shadow: 0 0 10px rgba(0, 255, 0, 0.5);
`;

const SystemInfo = styled.div`
  position: absolute;
  bottom: 5%;
  left: 5%;
  color: #666;
  font-size: 0.9rem;
  font-family: 'Courier New', monospace;
`;

interface LockScreenProps {
  onUnlock: () => void;
}

const LockScreen: React.FC<LockScreenProps> = ({ onUnlock }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isUnlocking, setIsUnlocking] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleUnlock = () => {
    setIsUnlocking(true);
    setTimeout(() => {
      onUnlock();
    }, 1000);
  };

  return (
    <LockScreenContainer isUnlocking={isUnlocking}>
      <TimeDisplay>
        {currentTime.toLocaleTimeString()}
      </TimeDisplay>
      
      <Logo3D>🐧</Logo3D>
      
      <WelcomeText>Welcome to Mukul's Portfolio</WelcomeText>
      <SubText>Cybersecurity Professional • Ethical Hacker • Developer</SubText>
      
      <UnlockButton onClick={handleUnlock}>
        🔓 Enter System
      </UnlockButton>
      
      <SystemInfo>
        Linux Portfolio OS v2.0.1<br/>
        Secure Boot: Enabled<br/>
        System Status: Ready
      </SystemInfo>
    </LockScreenContainer>
  );
};

export default LockScreen;
